import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { BillingInformation, EndOrderDetails, Order, TaxVoucher } from '@pedix-workspace/utils';

import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import {
  generateVoucher,
  arcaBusinessVatConditionOptions,
  getArcaVatConditionName,
  getArcaDocumentTypeName,
  isBillingInformationArca,
  generateCreditNote,
} from '@pedix-workspace/integrations-arca';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { OrderDetailsTaxVoucherActionsComponent } from './order-details-tax-voucher-actions/order-details-tax-voucher-actions.component';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { ToastrService } from 'ngx-toastr';
import { IconBillingVoucherComponent } from '@pedix-workspace/angular-ui-icons';

export type OrderListDetailRenderEvent = {
  row: Order;
  height: number;
};

@Component({
  selector: 'pxw-order-details-billing',
  templateUrl: './order-details-billing.component.html',
  styleUrls: ['./order-details-billing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    ButtonComponent,
    OrderDetailsTaxVoucherActionsComponent,
    IconBillingVoucherComponent,
  ],
})
export class OrderDetailsBillingComponent {
  private environmentService = inject(EnvironmentService);
  private toastrService = inject(ToastrService);
  private t = inject(TranslocoService);

  readonly orderDetails = input.required<EndOrderDetails>();
  readonly billingInformation = input.required<BillingInformation | null | undefined>();

  readonly adminMode = input(false);

  readonly taxVoucherUpdated = output<TaxVoucher>();

  readonly generatingTaxVoucher = signal(false);
  readonly generatingCreditNote = signal(false);

  readonly taxVoucherId = computed(() => this.orderDetails().taxVoucher?.id);
  readonly taxVoucherBilled = computed(() => !!this.orderDetails().taxVoucher);

  readonly vatCondition = computed(() => {
    const billingInformation = this.billingInformation();

    if (isBillingInformationArca(billingInformation)) {
      return getArcaVatConditionName(billingInformation.vatCondition);
    }

    return billingInformation?.vatCondition || '--';
  });
  readonly billingName = computed(() => {
    const billingInformation = this.billingInformation();

    return billingInformation?.name || '--';
  });
  readonly billingDocument = computed(() => {
    const billingInformation = this.billingInformation();

    if (!billingInformation?.documentType || !billingInformation?.documentNumber) {
      return '--';
    }

    return `${billingInformation.documentType} - ${billingInformation.documentNumber}`;
  });
  readonly documentType = computed(() => {
    const billingInformation = this.billingInformation();

    if (isBillingInformationArca(billingInformation)) {
      return getArcaDocumentTypeName(billingInformation.documentType);
    }

    return billingInformation?.documentType || '--';
  });
  readonly billingAddress = computed(() => {
    const billingInformation = this.billingInformation();

    return billingInformation?.address || '--';
  });
  readonly isVatConditionBusiness = computed(() => {
    return !!arcaBusinessVatConditionOptions.find(
      option => option.id === this.billingInformation()?.vatCondition,
    );
  });
  readonly requiredDataCompleted = computed(() => {
    const billingInformation = this.billingInformation();

    if (!billingInformation) {
      return false;
    }
    if (this.isVatConditionBusiness()) {
      return billingInformation.name && billingInformation.documentNumber;
    }
    return true;
  });
  readonly isNotCanceled = computed(() => {
    return this.orderDetails().status !== 'canceled';
  });

  async onGenerateTaxVoucher() {
    this.generatingTaxVoucher.set(true);

    try {
      const { taxVoucher } = await generateVoucher(this.environmentService.apiIntegrationsUrl, {
        establishmentId: this.orderDetails().establishmentId,
        orderIds: [this.orderDetails().id!],
        billingInformation: this.billingInformation()!,
      });

      this.taxVoucherUpdated.emit(taxVoucher);

      this.toastrService.success('Comprobante fiscal generado con éxito');
    } catch (error) {
      this.handleArcaError(error);
    }
    this.generatingTaxVoucher.set(false);
  }

  async onGenerateCreditNote() {
    this.generatingCreditNote.set(true);

    try {
      const { taxVoucher } = await generateCreditNote(this.environmentService.apiIntegrationsUrl, {
        establishmentId: this.orderDetails().establishmentId,
        taxVoucherId: this.taxVoucherId()!,
        reason: 'Cancelación de factura',
      });

      this.taxVoucherUpdated.emit(taxVoucher);

      this.toastrService.success('Comprobante fiscal generado con éxito');
    } catch (error) {
      this.handleArcaError(error);
    }
    this.generatingCreditNote.set(false);
  }

  private handleArcaError(error: any) {
    if (isArcaUnkownError(error)) {
      this.toastrService.error(error.details.Msg, 'Error: ' + error.details.Code);
    } else if (isArcaVoucherDataError(error)) {
      this.toastrService.error(
        error.observaciones.map(o => o.code + ': ' + o.message).join('\n'),
        error.message,
      );
    } else if (isGenericError(error)) {
      this.toastrService.error(error.message, this.t.translate('error.title'));
    } else {
      this.toastrService.error('Error desconocido al generar el comprobante');
    }
    console.error(error);
  }
}

type ArcaUnkownError = {
  details: {
    Code: string;
    Msg: string;
  };
};
type ArcaCoucherDataError = {
  message: string;
  resultado: string;
  observaciones: {
    code: string;
    message: string;
  }[];
};

function isArcaUnkownError(error: any): error is ArcaUnkownError {
  if ('details' in error && error.details?.Code && error.details?.Msg) {
    return true;
  }

  return false;
}
function isArcaVoucherDataError(error: any): error is ArcaCoucherDataError {
  if ('message' in error && 'resultado' in error && 'observaciones' in error) {
    return true;
  }

  return false;
}
function isGenericError(error: any): error is { message: string } {
  if ('message' in error) {
    return true;
  }

  return false;
}
